import React from 'react'
import { useAppSelector } from '../../../hooks/redux'
import { useSnackbar, SnackbarContent } from 'notistack'
import { useHistory } from 'react-router'

import Button from '../../atoms/Button2'
import OverflowText from '../../atoms/OverflowText'

import { Routes } from '../../../Routes'
import { TYPES } from '../../../providers/AppNotificationProvider'
import {
  findFilterID,
  formatLastMessageForAppNotification,
  getAppAlertLatestEvent
} from '../../../utilities'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useBreakpoint } from '../../../providers/BreakpointProvider'
import { selectConvo } from '../../../slices/convo'
import useStyles from './styles'

const INIT_BY = {
  integration: 'Live Chat',
  email: 'Email',
  sms: 'SMS'
}

const TITLE = {
  NEW_CHAT: 'Incoming'
}

const AppNotificationV2 = ({ id, type, msg, onClick }, ref) => {
  const history = useHistory()
  const { closeSnackbar } = useSnackbar()
  const { updated } = useAppSelector(selectConvo)
  const { isSmall } = useBreakpoint()
  const { getUsers, allUsers } = useUserAPI()
  const session = React.useMemo(() => updated[msg?.id], [updated, msg])

  const classes = useStyles()

  const handleClose = React.useCallback(
    e => {
      e.stopPropagation()
      closeSnackbar(id)
    },
    [closeSnackbar, id]
  )

  const handleAcceptClick = React.useCallback(
    e => {
      handleClose(e)
      onClick && onClick()

      if (type === TYPES.NEW_CHAT) {
        history.replace(`${Routes.CLIENT_CHATS}?session=${msg.session}`)
        return
      }
    },
    [handleClose, history, msg, onClick, type]
  )

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const filter = findFilterID(msg?.owner_id, false, msg?.id)
    const latest = getAppAlertLatestEvent(session, filter)
    return latest
  }, [msg, session])

  const lastText = React.useMemo(
    () => formatLastMessageForAppNotification(lastMessage, allAgents, allUsers),
    [lastMessage, allAgents, allUsers]
  )

  React.useEffect(() => {
    if (type === TYPES.NEW_CHAT && session && session.userIds.length > 1) {
      closeSnackbar(id)
    }
  }, [session, type, id, closeSnackbar])

  return (
    <SnackbarContent ref={ref} className={classes.root}>
      <div className={classes.content}>
        <div className={classes.header}>
          <h1>{TITLE[type]}</h1>
          {msg.initiated_by && (
            <div className={classes.pill}>
              {INIT_BY[msg.initiated_by] || INIT_BY['integration']}
            </div>
          )}
        </div>
        <div className={classes.message}>
          <span className={classes.messageTextContainer}>
            <div>
              <OverflowText className={classes.messageTitle}>
                {msg?.user?.name || ''} {msg?.topic && `| ${msg.topic}`}
              </OverflowText>
            </div>
            {lastText && (
              <OverflowText>
                <div className={classes.messageSubTitle}> {lastText}</div>
              </OverflowText>
            )}
          </span>
          {!isSmall && (
            <span className={classes.buttonContainer}>
              <div className={classes.ignoreBtn} onClick={handleClose}>
                Ignore
              </div>
              <Button onClick={handleAcceptClick}>Accept</Button>
            </span>
          )}
        </div>
        {isSmall && (
          <span className={classes.buttonContainer}>
            <div className={classes.ignoreBtn} onClick={handleClose}>
              Ignore
            </div>
            <Button onClick={handleAcceptClick}>Accept</Button>
          </span>
        )}
      </div>
    </SnackbarContent>
  )
}

export default React.forwardRef(AppNotificationV2)
